import { Link, useRouter } from '@tanstack/react-router'
import { Botao } from '@ui/glass/Botao'

/**
 * A saída de uma tela empilhada.
 *
 * ## Um só, e não um por tela
 *
 * Havia três cópias idênticas — em `Produto`, `Grupo` e `Nota` —, cada uma com
 * o mesmo `className` escrito à mão. Três cópias são três chances de uma ganhar
 * um espaçamento diferente, e foi assim que a de 28px de altura sobreviveu à
 * varredura de alvos de toque: ela não era um `<button>`, e cada arquivo tinha
 * a sua.
 *
 * ## Ele VOLTA, e não vai para um lugar fixo
 *
 * Era um `<Link to="/produtos">`, e isso é outra coisa: quem chegou à página de
 * um produto **pelos Lembretes** tocava em "voltar" e ia parar no catálogo — um
 * lugar onde nunca esteve, com o item que estava comprando para trás. Visto em
 * uso.
 *
 * Agora, havendo histórico, ele desfaz o último passo de verdade. `para` virou
 * o **destino de queda**: abrir o link direto, recarregar a página ou entrar
 * pelo app instalado começa sem histórico nenhum, e aí um botão que não faz
 * nada seria pior que um que leva ao lugar mais provável.
 *
 * ## E o rótulo muda junto, porque senão ele mente
 *
 * Voltando pelo histórico não dá para saber o nome do que vem atrás, então ele
 * diz "Voltar". Só no caso de queda — quando o destino é sabidamente `para` —
 * é que ele nomeia o lugar, à moda do iOS. Um botão escrito "Produtos" que
 * leva aos Lembretes é exatamente o defeito que este trecho conserta.
 *
 * ## O `aria-label` diz "voltar para", e o rótulo visível diz só o lugar
 *
 * A aba "Produtos" da barra de baixo e o voltar de um produto tinham o **mesmo
 * nome acessível** e o mesmo destino, significando coisas diferentes — quem
 * navega por lista de links ouvia "Produtos, Produtos". Na tela o contexto
 * resolve (um está no topo, com um chevron); para quem não a vê, resolve o
 * rótulo.
 *
 * ## É alvo de dedo, e por isso tem 44px
 *
 * `Docs/04-Design/Navegação.md`: *"iOS instalado não tem gesto de voltar
 * confiável e não existe controle visível — então toda tela empilhada desenha o
 * próprio Voltar."* Sendo a única saída, ela não pode ser difícil de acertar.
 *
 * ## Ele é a PÍLULA do sistema, e não um link sublinhado
 *
 * Era `text-sm underline` — a mesma cara de um link dentro de um parágrafo,
 * numa coisa que é a saída da tela. A decisão 232 já dizia que ação de tela usa
 * `Botao`, e voltar é ação de tela: existe uma vez por viewport. `flat`, e não
 * `frost`, porque o desfoque é o que custa no compositor e num controle de
 * 44px ele quase não aparece.
 *
 * ## A seta é `‹`, e não `←`
 *
 * A flecha desenha uma linha inteira e disputa peso com o rótulo; o chevron é o
 * mesmo glifo que a `Dobra` usa para "abre para lá", apontando para trás. Um
 * sistema, um vocabulário de setas.
 */
export function Voltar({ para, children }: { para: string; children: string }) {
  const router = useRouter()
  // Lido no render, e não guardado: o valor vale para esta visita à tela, e uma
  // tela empilhada se abre uma vez.
  const temHistorico = router.history.canGoBack()

  const seta = (
    <span aria-hidden="true" className="text-sm leading-none">
      ‹
    </span>
  )

  if (temHistorico) {
    return (
      <Botao
        type="button"
        onClick={() => {
          router.history.back()
        }}
        data-voltar
        className="text-xs"
      >
        {seta}
        Voltar
      </Botao>
    )
  }

  return (
    <Botao as={Link} to={para} aria-label={`Voltar para ${children}`} data-voltar className="text-xs">
      {seta}
      {children}
    </Botao>
  )
}
